import React from 'react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Checkbox } from '../ui/checkbox';
import { ScrollArea } from '../ui/scroll-area';
import { Loader2, Target, XCircle } from 'lucide-react';
import { STAGE_BY_ID } from './constants';
import { useCanSeeRevenue } from '../../lib/perms';

/**
 * Stage rich-detail list — shown when a specific stage tab is selected.
 * Renders a checkbox-table style list with full candidate context per row,
 * a sticky bulk-action bar, and a click-to-open right-panel hand-off.
 */
const StageDetailList = ({
  stage,
  items,
  loading,
  selectedAppId,
  onSelect,
  bulkSelected,
  onToggleRow,
  onToggleAll,
  onClearBulk,
  onBulkAction,
}) => {
  const canSeeRevenue = useCanSeeRevenue();
  const allChecked = items.length > 0 && bulkSelected.size === items.length;
  const someChecked = bulkSelected.size > 0 && bulkSelected.size < items.length;
  const stageLabel = stage?.label || stage?.id || '';

  // Stages a recruiter is most likely to move TO from the current stage —
  // shown as quick-action buttons in the bulk bar
  const STAGE_FORWARD_MAP = {
    sourced: ['screening', 'client_submission'],
    screening: ['client_submission', 'interview_scheduled'],
    client_submission: ['interview_scheduled'],
    interview_scheduled: ['interview_selected'],
    interview_selected: ['offer_released'],
    offer_released: ['joined'],
    joined: [],
    rejected: ['sourced'],
    dropped: ['sourced'],
  };
  const forwardTargets = (STAGE_FORWARD_MAP[stage?.id] || []).map((id) => STAGE_BY_ID[id]).filter(Boolean);

  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center text-slate-400" data-testid="cc-stage-list-loading">
        <Loader2 className="w-5 h-5 animate-spin mr-2" />
        <span className="text-sm">Loading {stageLabel} candidates…</span>
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center text-center px-6 py-12" data-testid="cc-stage-list-empty">
        <div className={`w-12 h-12 rounded-full flex items-center justify-center mb-3 ${stage?.col || 'bg-slate-50'}`}>
          <Target className="w-5 h-5 text-slate-500" />
        </div>
        <p className="text-sm font-semibold text-slate-700">No {stageLabel.toLowerCase()} candidates</p>
        <p className="text-xs text-slate-500 mt-1 max-w-sm">
          Once you move candidates into <span className="font-medium">{stageLabel}</span>, they{"\u2019"}ll appear here with all their key details.
        </p>
      </div>
    );
  }

  return (
    <div className="flex-1 min-h-0 flex flex-col" data-testid="cc-stage-list">
      {/* Bulk action bar */}
      {bulkSelected.size > 0 && (
        <div
          className="px-3 py-2 bg-teal-50 border-b border-teal-200 flex items-center gap-2 flex-wrap"
          data-testid="cc-bulk-bar"
        >
          <span className="text-xs font-semibold text-teal-800" data-testid="cc-bulk-count">
            {bulkSelected.size} selected
          </span>
          <Button
            variant="ghost"
            size="sm"
            onClick={onClearBulk}
            className="text-xs h-7 px-2 text-teal-700 hover:bg-teal-100"
            data-testid="cc-bulk-clear"
          >
            Clear
          </Button>
          <span className="ml-auto flex items-center gap-1.5 flex-wrap">
            {forwardTargets.map((t) => (
              <Button
                key={t.id}
                size="sm"
                variant="default"
                onClick={() => onBulkAction('move', t.id)}
                className="h-7 text-xs"
                data-testid={`cc-bulk-move-${t.id}`}
              >
                <span className={`w-1.5 h-1.5 rounded-full ${t.dot} mr-1.5`} />
                Move to {t.label}
              </Button>
            ))}
            {stage?.id !== 'rejected' && (
              <Button
                size="sm"
                variant="destructive"
                onClick={() => onBulkAction('reject', 'rejected')}
                className="h-7 text-xs"
                data-testid="cc-bulk-reject"
              >
                <XCircle className="w-3.5 h-3.5 mr-1" />
                Reject
              </Button>
            )}
          </span>
        </div>
      )}

      {/* Header row */}
      <div className="px-3 py-1.5 bg-slate-50 border-b border-slate-200 flex items-center gap-3 text-[10px] uppercase tracking-wider text-slate-500 font-bold">
        <Checkbox
          checked={allChecked || (someChecked && 'indeterminate')}
          onCheckedChange={onToggleAll}
          aria-label="Select all"
          data-testid="cc-stage-select-all"
        />
        <span className="flex-1 min-w-0">Candidate</span>
        <span className="hidden md:block w-32 truncate">Job · Client</span>
        <span className="hidden lg:block w-20 text-right">{canSeeRevenue ? 'Exp / CTC' : 'Exp'}</span>
        <span className="hidden xl:block w-24 truncate">Location</span>
        <span className="w-20 text-right">In stage</span>
      </div>

      {/* Rows */}
      <ScrollArea className="flex-1">
        <ul className="divide-y divide-slate-100">
          {items.map((it, idx) => {
            const isChecked = bulkSelected.has(it.application_id);
            const isSelected = selectedAppId === it.application_id;
            const ctcLabel = !canSeeRevenue
              ? '—'
              : it.expected_ctc != null
              ? `₹${it.expected_ctc} L`
              : it.current_ctc != null
              ? `₹${it.current_ctc} L*`
              : '—';
            const expLabel = it.experience_years != null ? `${it.experience_years}y` : '—';
            const days = it.days_in_stage;
            const daysClass =
              days == null
                ? 'text-slate-400'
                : days >= 7
                ? 'text-rose-600 font-semibold'
                : days >= 3
                ? 'text-amber-600 font-semibold'
                : 'text-slate-600';
            return (
              <li
                key={it.application_id}
                data-testid={`cc-stage-row-${idx}`}
                className={`group flex items-center gap-3 px-3 py-2.5 cursor-pointer transition ${
                  isSelected ? 'bg-teal-50/70' : 'hover:bg-slate-50'
                }`}
                onClick={() => onSelect(it.application_id)}
              >
                <div onClick={(e) => e.stopPropagation()} className="flex-shrink-0">
                  <Checkbox
                    checked={isChecked}
                    onCheckedChange={() => onToggleRow(it.application_id)}
                    aria-label={`Select ${it.candidate_name}`}
                    data-testid={`cc-stage-row-check-${idx}`}
                  />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-semibold text-slate-900 truncate">
                      {it.candidate_name || 'Unnamed'}
                    </span>
                    {it.has_resume ? (
                      <Badge variant="outline" className="text-[9px] h-4 px-1 bg-emerald-50 text-emerald-700 border-emerald-200">CV</Badge>
                    ) : (
                      <Badge variant="outline" className="text-[9px] h-4 px-1 text-slate-400">No CV</Badge>
                    )}
                    {it.notice_period != null && (
                      <Badge variant="outline" className="text-[9px] h-4 px-1 text-slate-500" title="Notice period (days)">
                        {it.notice_period}d notice
                      </Badge>
                    )}
                  </div>
                  <div className="text-xs text-slate-500 truncate mt-0.5 flex items-center gap-2">
                    {it.current_designation ? (
                      <span className="truncate" title={it.current_designation}>{it.current_designation}</span>
                    ) : null}
                    {it.email ? (
                      <span className="hidden sm:inline truncate text-slate-400" title={it.email}>· {it.email}</span>
                    ) : null}
                  </div>
                  {it.last_activity_remark ? (
                    <div className="text-[11px] text-slate-400 truncate mt-0.5 italic" title={it.last_activity_remark}>
                      {"\u201c"}{it.last_activity_remark}{"\u201d"}
                    </div>
                  ) : null}
                </div>

                <div className="hidden md:block w-32 text-xs text-slate-600 truncate">
                  <div className="font-medium truncate" title={it.job_title}>{it.job_title || '—'}</div>
                  <div className="text-slate-400 truncate" title={it.client_name}>{it.client_name || '—'}</div>
                </div>

                <div className="hidden lg:block w-20 text-right text-xs">
                  <div className="font-semibold text-slate-700">{expLabel}</div>
                  <div className="text-slate-500">{ctcLabel}</div>
                </div>

                <div className="hidden xl:block w-24 text-xs text-slate-600 truncate" title={it.location}>
                  {it.location || '—'}
                </div>

                <div className={`w-20 text-right text-xs ${daysClass}`}>
                  {days == null ? '—' : `${days}d`}
                </div>
              </li>
            );
          })}
        </ul>
      </ScrollArea>
    </div>
  );
};

export default StageDetailList;
